import { useState } from "react";
import { useNavigate } from "react-router-dom";
import Button from "../../Elements/Button/Button";
import Input from "../../Elements/Input/Input";
import MapPickerLeaflet from "../../Sections/createRestaurant/MapPickerLeaflet/MapPickerLeaflet";
import { useAuthentication } from "../../../Hooks/useAuthentication";

/**
 * Pantalla obligatoria post-login Google.
 * No se puede cerrar hasta que phone (10 dígitos) y dirección (con lat/lng) estén completos.
 * onSave({ phone, delivery: { direccion, lat, lng } })
 */
export default function CompleteProfileModal({ userName, onSave }) {
  const { removeToken } = useAuthentication();
  const navigate = useNavigate();

  const [phone, setPhone] = useState("");
  const [phoneError, setPhoneError] = useState("");

  const [address, setAddress] = useState("");
  const [lat, setLat] = useState(null);
  const [lng, setLng] = useState(null);
  const [addressError, setAddressError] = useState("");
  const [showMap, setShowMap] = useState(false);

  const [loading, setLoading] = useState(false);

  // El componente Input llama onChange con el valor string directamente
  const handlePhoneChange = (val) => {
    const clean = (val || "").replace(/\D/g, "").slice(0, 10);
    setPhone(clean);
    if (clean.length === 10) setPhoneError("");
  };

  const handleMapPick = (pickedLat, pickedLng, pickedAddress) => {
    setLat(pickedLat);
    setLng(pickedLng);
    if (pickedAddress) setAddress(pickedAddress);
    setAddressError("");
    setShowMap(false);
  };

  const handleSave = async () => {
    let valid = true;

    if (phone.length < 10) {
      setPhoneError("Ingresa un teléfono válido de 10 dígitos");
      valid = false;
    }

    if (!lat || !lng) {
      setAddressError("Selecciona tu dirección en el mapa");
      valid = false;
    }

    if (!valid) return;

    setLoading(true);
    await onSave({
      phone,
      delivery: {
        direccion: address || "",
        lat,
        lng,
      },
    });
    setLoading(false);
  };

  const canSave = phone.length === 10 && lat !== null && lng !== null;

  return (
    <>
      {/* Pantalla completa — no cierra al hacer click afuera */}
      <div
        style={{
          position: "fixed",
          inset: 0,
          background: "rgba(0, 0, 0, 0.85)",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 9999,
          padding: "1rem",
        }}
      >
        <div
          className="card shadow-lg border-0"
          style={{ maxWidth: 460, width: "100%", borderRadius: 16 }}
        >
          <div className="card-body p-4 p-md-5">

            {/* Header */}
            <div className="text-center mb-4">
              <div style={{ fontSize: 44 }}>👋</div>
              <h5 className="fw-bold mb-1">
                ¡Hola, {userName || "bienvenido"}!
              </h5>
              <p className="text-muted mb-0" style={{ fontSize: 14 }}>
                Antes de continuar necesitamos dos datos para coordinar tus entregas.
              </p>
            </div>

            {/* Teléfono */}
            <div className="mb-3">
              <Input
                label="Teléfono"
                value={phone}
                onChange={handlePhoneChange}
                onBlur={() => {
                  if (phone.length > 0 && phone.length < 10)
                    setPhoneError("Ingresa un teléfono válido de 10 dígitos");
                }}
                inputMode="numeric"
                pattern="\d*"
                maxLength={10}
                placeholder="10 dígitos"
                error={phoneError}
                success={phone.length === 10}
              />
            </div>

            {/* Dirección de entrega */}
            <div className="mb-4">
              <Input
                label="Dirección de entrega"
                value={address}
                onClick={() => setShowMap(true)}
                readOnly
                placeholder="Toca para seleccionar en el mapa"
                error={addressError}
                success={!!(lat && lng)}
              />
              {lat && lng && (
                <small className="text-success d-block mt-1">
                  📍 Ubicación seleccionada correctamente
                </small>
              )}
              {!lat && !addressError && (
                <small className="text-muted d-block mt-1">
                  Toca el campo para abrir el mapa y marcar tu domicilio.
                </small>
              )}
            </div>

            {/* Botón guardar */}
            <div className="d-grid">
              <Button
                text={loading ? "Guardando..." : "Continuar"}
                color="green"
                fill
                action={handleSave}
                disabled={loading || !canSave}
                classNames="py-3"
              />
            </div>

            <p className="text-center text-muted mt-3 mb-0" style={{ fontSize: 12 }}>
              Estos datos son necesarios para usar BubUp. No puedes omitir este paso.
            </p>

            {/* Cerrar sesion */}
            <div className="d-grid mt-2">
              <Button
                text="Cerrar sesion"
                color="red"
                fill
                action={() => {
                  localStorage.removeItem("profileIncomplete");
                  removeToken();
                  navigate("/");
                  window.location.reload();
                }}
              />
            </div>

          </div>
        </div>
      </div>

      {/* Mapa — encima del modal */}
      {showMap && (
        <MapPickerLeaflet
          initialLat={lat || 19.432608}
          initialLng={lng || -99.133209}
          onPick={handleMapPick}
          onClose={() => setShowMap(false)}
        />
      )}
    </>
  );
}
